// Conversión tiempo ↔ x para las herramientas de dibujo, válida también en el
// espacio vacío a la derecha de la última vela («pared invisible», 2026-10-22).
//
// lightweight-charts v4 solo sabe convertir tiempos que EXISTEN como punto de
// la escala de tiempo: `coordinateToTime(x)` devuelve null a la derecha de la
// última vela y `timeToCoordinate(t)` devuelve null para un tiempo futuro. Las
// herramientas descartaban el punto con time == null, así que ningún trazo
// (línea, Fibonacci, rectángulo…) podía ir más allá de la última vela, aunque
// el chart sí pinte ese hueco (rightOffset).
//
// La escala lógica (`coordinateToLogical` / `logicalToCoordinate`) sí cubre ese
// hueco: cada índice lógico a la derecha de la última vela es una barra futura
// de la misma duración. Por eso, SOLO cuando la librería devuelve null y el
// punto cae a la derecha de la última vela, se extrapola:
//   tiempo = últimaVela + (índice − índiceÚltimaVela) × duraciónBarra
// Dentro del rango de datos el comportamiento es exactamente el de antes.
//
// La última vela se lee con `series.dataByIndex` (búsqueda binaria), no con
// `series.data()`, que copia todas las velas en cada llamada y esto corre en
// cada frame de render y en cada mousemove.

const NEAREST_LEFT = -1  // MismatchDirection.NearestLeft de lightweight-charts
const BAR_SAMPLE = 50

// Duración de una barra en segundos: el menor salto positivo entre tiempos
// consecutivos (con huecos de sesión, p. ej. NQ, el salto mínimo sigue siendo
// la barra; la media no lo sería).
export function barSecondsFromTimes(times) {
  let best = null
  for (let i = 1; i < times.length; i++) {
    const dt = times[i] - times[i - 1]
    if (Number.isFinite(dt) && dt > 0 && (best == null || dt < best)) best = dt
  }
  return best
}

// Ancla de la extrapolación: tiempo e índice lógico de la última vela, más la
// duración de barra. null si no hay datos suficientes.
function lastBarAnchor(chart, series) {
  if (typeof series?.dataByIndex !== 'function') return null
  const last = series.dataByIndex(Number.MAX_SAFE_INTEGER, NEAREST_LEFT)
  const lastTime = last?.time
  if (typeof lastTime !== 'number') return null
  const ts = chart.timeScale()
  const lastX = ts.timeToCoordinate(lastTime)
  if (lastX == null) return null
  const lastLogical = ts.coordinateToLogical(lastX)
  if (lastLogical == null) return null
  const times = [lastTime]
  for (let i = 1; i <= BAR_SAMPLE; i++) {
    const t = series.dataByIndex(lastLogical - i)?.time
    if (typeof t === 'number') times.unshift(t)
  }
  const barSeconds = barSecondsFromTimes(times)
  if (!barSeconds) return null
  return { lastTime, lastLogical, barSeconds }
}

// x (px del canvas) → tiempo UTC en segundos, o null.
export function xToTime(chart, series, x) {
  const ts = chart.timeScale()
  const time = ts.coordinateToTime(x)
  if (time != null) return time
  const anchor = lastBarAnchor(chart, series)
  if (!anchor) return null
  const logical = ts.coordinateToLogical(x)
  if (logical == null || logical <= anchor.lastLogical) return null
  const bars = Math.round(logical - anchor.lastLogical)
  return anchor.lastTime + bars * anchor.barSeconds
}

// tiempo UTC en segundos → x (px del canvas), o null.
export function timeToX(chart, series, time) {
  const ts = chart.timeScale()
  const x = ts.timeToCoordinate(time)
  if (x != null) return x
  const anchor = lastBarAnchor(chart, series)
  if (!anchor || !(time > anchor.lastTime)) return null
  const logical = anchor.lastLogical + (time - anchor.lastTime) / anchor.barSeconds
  return ts.logicalToCoordinate(logical)
}

// El `toXY` que reciben renderDrawing / hitTestDrawing.
export function makeToXY(chart, series) {
  return (time, price) => ({
    x: time  != null ? timeToX(chart, series, time) : null,
    y: price != null ? series.priceToCoordinate(price) : null,
  })
}
